import { all, call, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import LoadingStatus from '@libs/loading-status';

import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENTS_EXPORT__LIST_FETCH_REQUEST,
  DOCUMENTS_EXPORT__LIST_FETCH_RESTART,
  DOCUMENTS_EXPORT__LIST_FETCH_START,
  DOCUMENTS_EXPORT__LIST_FETCH_SUCCESS,
  DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_REQUEST,
} from '@app/store/actionTypes';
import {
  documentsExportFetchList,
  documentsExportFetchListEnd,
  documentsExportFetchListFailure,
  documentsExportFetchListStart,
  documentsExportFetchListSuccess,
  documentsExportFetchTotalDocumentsCountFailure,
  documentsExportFetchTotalDocumentsCountSuccess,
} from '@app/store/documentsExport/actions';
import {
  getAssigned,
  getLabelId,
  getLimit,
  getListFetchRange,
  getLoadingStatus,
  getMaxPage,
  getOffset,
  getStartIndex,
  getTotalDocumentsCount,
} from '@app/store/documentsExport/selectors';
import { getDocuments } from '@app/store/transport-layer/document/documents';

export function* fetchTotalDocumentsCount() {
  try {
    const assigned: ReturnType<typeof getAssigned> = yield select(getAssigned);
    const labelId: ReturnType<typeof getLabelId> = yield select(getLabelId);

    const { countOfDocuments }: { countOfDocuments: number } = yield call(
      getDocuments,
      workspaceService.getWorkspaceId(),
      {
        assigned,
        limit: 10,
        isArchived: false,
        offset: 0,
        labelId,
      }
    );
    yield put(documentsExportFetchTotalDocumentsCountSuccess(countOfDocuments));
  } catch (error) {
    yield put(documentsExportFetchTotalDocumentsCountFailure(error));
  }
}

export function* fetchDocumentsList({
  payload: { limit, offset },
}: ReturnType<typeof documentsExportFetchList>) {
  try {
    const assigned: ReturnType<typeof getAssigned> = yield select(getAssigned);
    const labelId: ReturnType<typeof getLabelId> = yield select(getLabelId);

    const { countOfDocuments, documents } = yield call(
      getDocuments,
      workspaceService.getWorkspaceId(),
      {
        assigned,
        limit,
        isArchived: false,
        labelId,
        offset,
      }
    );
    const maxPage = Math.ceil(countOfDocuments / limit) || 1;
    yield put(
      documentsExportFetchListSuccess({
        documentsList: documents,
        maxPage,
        totalDocumentsCount: countOfDocuments,
      })
    );
  } catch (error) {
    yield put(documentsExportFetchListFailure(error));
  }
}

export function* fetchDocumentsListRestart() {
  const startIndex: ReturnType<typeof getStartIndex> = yield select(getStartIndex);
  const offset = Math.floor(startIndex / 100) * 100;
  yield put(
    documentsExportFetchList({
      limit: 100,
      offset,
    })
  );
}

export function* fetchDocumentsListStart({
  payload: { limit, offset },
}: ReturnType<typeof documentsExportFetchListStart>) {
  yield put(
    documentsExportFetchList({
      limit,
      offset,
    })
  );
}

export function* fetchDocumentListNextPage() {
  const loadingStatus: ReturnType<typeof getLoadingStatus> = yield select(getLoadingStatus);

  if (loadingStatus === LoadingStatus.PENDING) {
    const limit: ReturnType<typeof getLimit> = yield select(getLimit);
    const maxPage: ReturnType<typeof getMaxPage> = yield select(getMaxPage);
    const currentOffset: ReturnType<typeof getOffset> = yield select(getOffset);
    const listFetchRange: ReturnType<typeof getListFetchRange> = yield select(getListFetchRange);

    const page = Math.ceil((currentOffset + 1) / limit);
    if (listFetchRange && page < listFetchRange.endPage && page < maxPage) {
      yield put(
        documentsExportFetchList({
          limit,
          offset: currentOffset + limit,
        })
      );
    } else {
      const totalDocumentsCount: ReturnType<typeof getTotalDocumentsCount> =
        yield select(getTotalDocumentsCount);
      yield put(documentsExportFetchListEnd({ totalDocumentsCount }));
    }
  }
}

export default function* sagas() {
  yield all([
    takeLeading(DOCUMENTS_EXPORT__LIST_FETCH_REQUEST, fetchDocumentsList),
    takeEvery(DOCUMENTS_EXPORT__LIST_FETCH_SUCCESS, fetchDocumentListNextPage),
    takeLeading(DOCUMENTS_EXPORT__LIST_FETCH_RESTART, fetchDocumentsListRestart),
    takeLeading(DOCUMENTS_EXPORT__LIST_FETCH_START, fetchDocumentsListStart),
    takeLatest(DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_REQUEST, fetchTotalDocumentsCount),
  ]);
}
